const API_BASE = import.meta.env.VITE_API_URL || '';

const getToken = () => localStorage.getItem('ako_token');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`,
});

// Centralized fetch wrapper with error handling
const apiFetch = async (url, options = {}) => {
  const res = await fetch(API_BASE + url, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || `Erreur ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const api = {
  // Auth (no token needed)
  login: (email, password) =>
    fetch(API_BASE + '/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }).then(r => r.json()),

  register: (name, email, password, phone, metier) =>
    fetch(API_BASE + '/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, phone, metier }),
    }).then(r => r.json()),

  // Clients
  getClients: () => apiFetch('/api/clients'),
  createClient: (data) => apiFetch('/api/clients', { method: 'POST', body: JSON.stringify(data) }),
  deleteClient: (id) => apiFetch(`/api/clients/${id}`, { method: 'DELETE' }),
  updateClient: (id, data) => apiFetch(`/api/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Invoices
  getInvoices: () => apiFetch('/api/invoices'),
  getInvoice: (id) => apiFetch(`/api/invoices/${id}`),
  createInvoice: (data) => apiFetch('/api/invoices', { method: 'POST', body: JSON.stringify(data) }),
  updateInvoiceStatus: (id, status) => apiFetch(`/api/invoices/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  createPaymentLink: (id) => apiFetch(`/api/invoices/${id}/payment-link`, { method: 'POST' }),

  // Quotes
  getQuotes: () => apiFetch('/api/quotes'),
  getQuote: (id) => apiFetch(`/api/quotes/${id}`),
  createQuote: (data) => apiFetch('/api/quotes', { method: 'POST', body: JSON.stringify(data) }),
  updateQuoteStatus: (id, status) => apiFetch(`/api/quotes/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  convertQuote: (id) => apiFetch(`/api/quotes/${id}/convert`, { method: 'POST' }),
  shareQuote: (id) => apiFetch(`/api/quotes/${id}/share`, { method: 'POST' }),
  getPublicQuote: (token) => fetch((import.meta.env.VITE_API_URL || '') + `/api/quotes/public/${token}`).then(r => r.json()),
  signQuote: (token, signatureData, signerName) =>
    fetch((import.meta.env.VITE_API_URL || '') + `/api/quotes/public/${token}/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signatureData, signerName }),
    }).then(r => r.json()),

  // Projects
  getProjects: () => apiFetch('/api/projects'),
  getKanban: () => apiFetch('/api/projects/kanban'),
  createProject: (data) => apiFetch('/api/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id, data) => apiFetch(`/api/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateProjectStatut: (id, statut) => apiFetch(`/api/projects/${id}/statut`, { method: 'PUT', body: JSON.stringify({ statut }) }),
  deleteProject: (id) => apiFetch(`/api/projects/${id}`, { method: 'DELETE' }),

  // Proposals
  getProposals: () => apiFetch('/api/proposals'),
  createProposal: (data) => apiFetch('/api/proposals', { method: 'POST', body: JSON.stringify(data) }),

  // Stats utilisateur
  getUserStats: () => apiFetch('/api/stats'),

  // Admin
  getUsers: () => apiFetch('/api/admin/users'),
  getStats: () => apiFetch('/api/admin/stats'),
  getArtisans: () => apiFetch('/api/admin/artisans'),
  createArtisan: (data) => apiFetch('/api/admin/artisans', { method: 'POST', body: JSON.stringify(data) }),

  // Artisan profile
  getArtisan: () => apiFetch('/api/artisan/profile'),
  updateArtisan: (data) => apiFetch('/api/artisan/profile', { method: 'PUT', body: JSON.stringify(data) }),

  // Auth
  changePassword: (currentPassword, newPassword) =>
    apiFetch('/api/auth/password', { method: 'PATCH', body: JSON.stringify({ currentPassword, newPassword }) }),

  // Profil & préférences
  getProfile: () => apiFetch('/api/auth/profile'),
  updateProfile: (data) => apiFetch('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(data) }),
  getPreferences: () => apiFetch('/api/auth/preferences'),
  updatePreferences: (data) => apiFetch('/api/auth/preferences', { method: 'PATCH', body: JSON.stringify(data) }),

  // Lien public facture
  shareInvoice: (id) => apiFetch(`/api/invoices/${id}/share`, { method: 'POST' }),
  getPublicInvoice: (token) => fetch((import.meta.env.VITE_API_URL || '') + `/api/public/invoices/${token}`).then(r => r.json()),

  // FEC export (Fichier des Écritures Comptables — art. L.47 A LPF)
  downloadFEC: async (annee) => {
    const res = await fetch(API_BASE + `/api/ecritures/fec?annee=${annee}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('Erreur génération FEC');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Le nom vient du header Content-Disposition
    const cd = res.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="([^"]+)"/);
    a.download = match ? match[1] : `FEC-${annee}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // CSV export
  exportCSV: async (type, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const url = API_BASE + `/api/${type}/export` + (qs ? '?' + qs : '');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error('Erreur export CSV');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${type}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },

  // PDF download
  downloadInvoicePdf: async (id, number) => {
    const res = await fetch(API_BASE + `/api/invoices/${id}/pdf`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('Erreur génération PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  downloadFacturX: async (id, number) => {
    const res = await fetch(API_BASE + `/api/invoices/${id}/facturx`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('Erreur génération Factur-X');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facturx-${number}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  },

  downloadQuotePdf: async (id, number) => {
    const res = await fetch(API_BASE + `/api/quotes/${id}/pdf`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('Erreur génération PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // 2FA
  get2faSetup: () => apiFetch('/api/auth/2fa/setup'),
  enable2fa: (code) => apiFetch('/api/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) }),
  disable2fa: (code) => apiFetch('/api/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) }),
  verify2faLogin: (temp_token, code) =>
    fetch((import.meta.env.VITE_API_URL || '') + '/auth/2fa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ temp_token, code }),
    }).then(r => r.json()),

  // Stripe
  getSubscription: () => apiFetch('/api/stripe/subscription'),
  createCheckout: (plan) => apiFetch('/api/stripe/checkout', { method: 'POST', body: JSON.stringify({ plan }) }),
  openPortal: () => apiFetch('/api/stripe/portal', { method: 'POST' }),

  // Plan & codes d'accès (utilisateur)
  getPlan: () => apiFetch('/api/plan'),
  activateCode: (code) => apiFetch('/api/access-codes/activate', { method: 'POST', body: JSON.stringify({ code }) }),
  getActiveCode: () => apiFetch('/api/access-codes/active'),

  // Comptabilité — catégories
  getCategories: () => apiFetch('/api/categories-charges'),
  createCategorie: (data) => apiFetch('/api/categories-charges', { method: 'POST', body: JSON.stringify(data) }),
  updateCategorie: (id, data) => apiFetch(`/api/categories-charges/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCategorie: (id) => apiFetch(`/api/categories-charges/${id}`, { method: 'DELETE' }),

  // Comptabilité — documents entrants
  getDocuments: (params = {}) => apiFetch('/api/documents-entrants?' + new URLSearchParams(params)),
  createDocument: (data) => apiFetch('/api/documents-entrants', { method: 'POST', body: JSON.stringify(data) }),
  updateDocument: (id, data) => apiFetch(`/api/documents-entrants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDocument: (id) => apiFetch(`/api/documents-entrants/${id}`, { method: 'DELETE' }),
  analyserDocument: (id) => apiFetch(`/api/documents-entrants/${id}/analyser`, { method: 'POST' }),
  uploadDocument: (file) => {
    const form = new FormData();
    form.append('fichier', file);
    return fetch((import.meta.env.VITE_API_URL || '') + '/api/documents-entrants/upload', {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    }).then(r => r.json());
  },

  // Comptabilité — écritures
  getEcritures: (params = {}) => apiFetch('/api/ecritures?' + new URLSearchParams(params)),
  createEcriture: (data) => apiFetch('/api/ecritures', { method: 'POST', body: JSON.stringify(data) }),
  deleteEcriture: (id) => apiFetch(`/api/ecritures/${id}`, { method: 'DELETE' }),

  // Portail client
  inviteClient: (data) => apiFetch('/api/portail/invite', { method: 'POST', body: JSON.stringify(data) }),
  getPortailTokens: () => apiFetch('/api/portail/list'),
  revokePortailToken: (id) => apiFetch(`/api/portail/${id}`, { method: 'DELETE' }),
  getPublicPortail: (token) => fetch((import.meta.env.VITE_API_URL || '') + `/api/portail/public/${token}`).then(r => r.json()),

  // Achats & Dépenses
  getDepenses: (params = {}) => apiFetch('/api/depenses?' + new URLSearchParams(params)),
  createDepense: (data) => apiFetch('/api/depenses', { method: 'POST', body: JSON.stringify(data) }),
  updateDepense: (id, data) => apiFetch(`/api/depenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDepense: (id) => apiFetch(`/api/depenses/${id}`, { method: 'DELETE' }),
  getDepensesStats: (params = {}) => apiFetch('/api/depenses/stats?' + new URLSearchParams(params)),

  // Agenda & RDV
  getAppointments: (params = {}) => apiFetch('/api/appointments?' + new URLSearchParams(params)),
  createAppointment: (data) => apiFetch('/api/appointments', { method: 'POST', body: JSON.stringify(data) }),
  updateAppointment: (id, data) => apiFetch(`/api/appointments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateAppointmentStatut: (id, statut) => apiFetch(`/api/appointments/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
  deleteAppointment: (id) => apiFetch(`/api/appointments/${id}`, { method: 'DELETE' }),
  notifyAppointment: (id, email) => apiFetch(`/api/appointments/${id}/notify`, { method: 'POST', body: JSON.stringify({ email }) }),

  // Catalogue produits & services
  getCatalog: (params = {}) => apiFetch('/api/catalog?' + new URLSearchParams(params)),
  createCatalogItem: (data) => apiFetch('/api/catalog', { method: 'POST', body: JSON.stringify(data) }),
  updateCatalogItem: (id, data) => apiFetch(`/api/catalog/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleCatalogFavori: (id) => apiFetch(`/api/catalog/${id}/favori`, { method: 'PATCH' }),
  deleteCatalogItem: (id) => apiFetch(`/api/catalog/${id}`, { method: 'DELETE' }),

  // Comptabilité — bilan
  getBilan: (params = {}) => apiFetch('/api/bilan?' + new URLSearchParams(params)),

  // Comptabilité — déclaration TVA
  getTvaDeclaration: (annee) => apiFetch(`/api/tva-declaration?annee=${annee}`),

  // Comptabilité — déclaration CA micro-entrepreneur (URSSAF)
  getCaDeclaration: (annee, categorie) => apiFetch(`/api/ca-declaration?annee=${annee}&categorie=${categorie}`),

  // Comptabilité — exercices / clôture
  getExercices: () => apiFetch('/api/exercices'),
  cloturerExercice: (annee) => apiFetch(`/api/exercices/${annee}/cloture`, { method: 'POST' }),
  rouvrirExercice: (annee) => apiFetch(`/api/exercices/${annee}/cloture`, { method: 'DELETE' }),

  // Admin — codes d'accès
  listCodes: () => apiFetch('/api/admin/access-codes'),
  generateCodes: (plan, count) => apiFetch('/api/admin/access-codes', { method: 'POST', body: JSON.stringify({ plan, count }) }),
  deleteCode: (id) => apiFetch(`/api/admin/access-codes/${id}`, { method: 'DELETE' }),

  // Analytics
  getAnalytics: () => apiFetch('/api/stats/analytics'),
  getOverdueInvoices: () => apiFetch('/api/invoices-overdue'),

  // Contrats
  getContracts: () => apiFetch('/api/contracts'),
  createContract: (data) => apiFetch('/api/contracts', { method: 'POST', body: JSON.stringify(data) }),
  uploadContract: (file, contractId, title, clientId) => {
    const form = new FormData();
    form.append('file', file);
    if (contractId) form.append('contract_id', contractId);
    if (title) form.append('title', title);
    if (clientId) form.append('client_id', clientId);
    return fetch((import.meta.env.VITE_API_URL || '') + '/api/contracts/upload', {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    }).then(r => r.json());
  },
  shareContract: (id) => apiFetch(`/api/contracts/${id}/share`, { method: 'POST' }),
  deleteContract: (id) => apiFetch(`/api/contracts/${id}`, { method: 'DELETE' }),
  getPublicContract: (token) => fetch((import.meta.env.VITE_API_URL || '') + `/api/contracts/public/${token}`).then(r => r.json()),
  signContract: (token, signatureData, signerName) =>
    fetch((import.meta.env.VITE_API_URL || '') + `/api/contracts/public/${token}/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signatureData, signerName }),
    }).then(r => r.json()),

  // Livre de Recettes
  getLivreRecettes: (annee, mois) => {
    const params = new URLSearchParams();
    if (annee) params.set('annee', annee);
    if (mois)  params.set('mois', mois);
    return apiFetch(`/api/livre-recettes?${params}`);
  },
  getLivreRecettesTotaux: (annee, mois) => {
    const params = new URLSearchParams();
    if (annee) params.set('annee', annee);
    if (mois)  params.set('mois', mois);
    return apiFetch(`/api/livre-recettes/totaux?${params}`);
  },
  addLivreRecette: (entry) => apiFetch('/api/livre-recettes', { method: 'POST', body: JSON.stringify(entry) }),
  deleteLivreRecette: (id) => apiFetch(`/api/livre-recettes/${id}`, { method: 'DELETE' }),
};

export const n8n = {
  sendRequest: (type, data) =>
    fetch(import.meta.env.VITE_N8N_WEBHOOK || 'https://n8n.akodigitale.fr/webhook/facture-devis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, ...data }),
    }).then(r => r.json()),
};
